import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const initialRole = loc.state?.role === "driver" ? "driver" : "customer";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: initialRole });
  const [accepted, setAccepted] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError("You must accept the Terms of Service and Privacy Policy to continue."); return; }
    if (!ageOk) { setError("You must confirm you are 18 or older."); return; }
    setError("");
    setBusy(true);
    try {
      const u = await register({ ...form, accepted_terms: true, accepted_privacy: true });
      if (u.role === "driver") nav("/driver");
      else nav("/customer");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2" style={{ background: "linear-gradient(135deg, #061224 0%, #0B3087 60%, #E62E2D 130%)" }}>
        <Logo />
        <div className="text-white">
          <h2 className="font-display text-5xl uppercase leading-tight">Join the<br/><span className="text-asap-yellow">roadrunner crew.</span></h2>
          <p className="text-white/70 mt-4 max-w-md">Customers get help fast. Drivers earn fast. Pick your side.</p>
        </div>
        <div className="text-white/40 text-xs">© ASAP Roadside</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
          <div className="lg:hidden mb-6"><Logo /></div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">Create Account</div>
            <h1 className="font-display text-3xl uppercase text-asap-deep mt-1">Sign Up</h1>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <RoleBtn active={form.role === "customer"} onClick={() => setForm({ ...form, role: "customer" })} testid="role-customer">I need help</RoleBtn>
            <RoleBtn active={form.role === "driver"} onClick={() => setForm({ ...form, role: "driver" })} testid="role-driver">I'm a driver</RoleBtn>
          </div>

          <Field label="Full Name">
            <input data-testid="reg-name" required value={form.name} onChange={change("name")} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="Jane Smith" />
          </Field>
          <Field label="Email">
            <input data-testid="reg-email" type="email" required value={form.email} onChange={change("email")} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="you@example.com" />
          </Field>
          <Field label="Phone">
            <input data-testid="reg-phone" value={form.phone} onChange={change("phone")} className="w-full px-4 py-3 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="(555) 555-5555" />
          </Field>
          <Field label="Password (min 6)">
            <div className="relative">
              <input data-testid="reg-password" type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={change("password")} className="w-full px-4 py-3 pr-12 border-2 border-asap-deep focus:border-asap-orange focus:outline-none" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw(!showPw)} data-testid="reg-toggle-pw" className="absolute right-3 top-1/2 -translate-y-1/2 text-asap-deep hover:text-asap-orange" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </Field>

          {error && <div data-testid="reg-error" className="text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2">{error}</div>}

          <div className="space-y-2 border-2 border-asap-deep p-3 bg-asap-yellow/10">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input data-testid="reg-age" type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} className="mt-1 w-4 h-4 accent-asap-orange"/>
              <span className="text-slate-700">I confirm I am at least <strong className="text-asap-deep">18 years of age</strong> and legally able to enter binding contracts.</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input data-testid="reg-terms" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 w-4 h-4 accent-asap-orange"/>
              <span className="text-slate-700">I have read and agree to the <Link to="/legal#terms" target="_blank" className="text-asap-orange font-bold underline">Terms of Service</Link> and <Link to="/legal#privacy" target="_blank" className="text-asap-orange font-bold underline">Privacy Policy</Link>{form.role === "driver" && <>, and the <Link to="/legal#driver-agreement" target="_blank" className="text-asap-orange font-bold underline">Driver Independent Contractor Agreement</Link></>}.</span>
            </label>
          </div>

          <button data-testid="reg-submit" type="submit" disabled={busy} className="w-full px-6 py-3 bg-asap-yellow text-asap-deep font-display uppercase tracking-wide border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all disabled:opacity-60">
            {busy ? "Creating…" : "Create Account →"}
          </button>

          <div className="text-sm text-slate-600">
            Already have one? <Link data-testid="reg-to-login" to="/login" className="font-bold text-asap-orange hover:underline">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-asap-deep mb-1">{label}</div>
      {children}
    </label>
  );
}

function RoleBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-3 font-display uppercase text-sm tracking-wide border-2 transition-all ${active ? "bg-asap-yellow text-asap-deep border-asap-deep shadow-hard-sm" : "bg-white text-asap-deep border-slate-300 hover:border-asap-orange"}`}
    >
      {children}
    </button>
  );
}
